
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatPrice, getStatusColor } from "@/components/admin/bills/utils";
import { Bill } from "../types";
import { useTaxRate } from "@/hooks/useTaxRate";
import { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InvoiceDialog } from "@/components/admin/bills/InvoiceDialog";

interface TransactionsTableProps {
  bills: Bill[];
  loading: boolean;
}

export const TransactionsTable = ({ bills, loading }: TransactionsTableProps) => {
  const { taxRate } = useTaxRate();
  const [totals, setTotals] = useState({
    paid: 0,
    unpaid: 0,
    paidCount: 0,
    unpaidCount: 0
  });
  
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);

  // Calculate totals when bills change
  useEffect(() => {
    if (bills && bills.length > 0) {
      const newTotals = bills.reduce((acc, bill) => {
        // Use the full amount directly from the bill
        const totalAmount = bill.amount;
        
        if (bill.status === 'paid') {
          return {
            ...acc,
            paid: acc.paid + totalAmount,
            paidCount: acc.paidCount + 1
          };
        } else if (bill.status === 'unpaid') {
          return {
            ...acc,
            unpaid: acc.unpaid + totalAmount,
            unpaidCount: acc.unpaidCount + 1
          };
        }
        return acc;
      }, { paid: 0, unpaid: 0, paidCount: 0, unpaidCount: 0 });
      
      setTotals(newTotals);
      
      // Log the calculated totals for verification
      console.log('Transaction History Revenue Calculation:', {
        totalPaidRevenue: formatPrice(newTotals.paid),
        totalPaidTransactions: newTotals.paidCount,
        totalUnpaidRevenue: formatPrice(newTotals.unpaid),
        totalUnpaidTransactions: newTotals.unpaidCount,
        individualBills: bills.map(bill => ({
          id: bill.id,
          transaction: bill.transaction_number,
          status: bill.status,
          amount: formatPrice(bill.amount)
        }))
      });
    }
  }, [bills]);

  const handleViewInvoice = (bill: Bill) => {
    // Convert Bill to AdminBill format for the InvoiceDialog
    const adminBill = {
      ...bill,
      client: { nama_sekolah: "Your School" }, // This is a placeholder as we don't have client info in this view
      created_at: bill.created_at || new Date().toISOString(),
      subscription_id: bill.subscription_id,
      user_id: bill.user_id,
    };
    
    setSelectedBill(adminBill as any);
    setIsInvoiceDialogOpen(true);
  };
  
  const formatDiscountDisplay = (bill: Bill) => {
    if (!bill.discount || bill.discount <= 0) return formatPrice(0);
    
    let percentageText = '';
    
    // Use the correct percentage value if available
    if (bill.discount_type === 'percentage') {
      // If discount_percentage exists but seems to be the actual amount instead of percentage
      if (typeof bill.discount_percentage === 'number') {
        if (bill.discount_percentage > 100) {
          // This is likely an error - the discount_percentage contains the amount
          // Calculate the actual percentage
          const originalPrice = bill.plan_price || (bill.plan?.price || 0);
          if (originalPrice > 0) {
            const calculatedPercentage = Math.round((bill.discount / originalPrice) * 100);
            percentageText = ` (${calculatedPercentage}%)`;
          }
        } else {
          // Normal case - use the provided percentage
          percentageText = ` (${bill.discount_percentage}%)`;
        }
      } else {
        // Fallback if no discount_percentage provided
        const originalPrice = bill.plan_price || (bill.plan?.price || 0);
        if (originalPrice > 0) {
          const calculatedPercentage = Math.round((bill.discount / originalPrice) * 100);
          percentageText = ` (${calculatedPercentage}%)`;
        }
      }
    } else {
      // Calculate percentage for fixed discounts based on plan price
      const originalPrice = bill.plan_price || (bill.plan?.price || 0);
      if (originalPrice > 0) {
        const calculatedPercentage = Math.round((bill.discount / originalPrice) * 100);
        percentageText = ` (${calculatedPercentage}%)`;
      }
    }
    
    return `${formatPrice(bill.discount)}${percentageText}`;
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      {totals.paidCount > 0 && (
        <div className="mb-4 p-4 bg-gray-50 rounded-md">
          <h3 className="font-medium text-gray-800">Revenue Verification</h3>
          <p className="text-sm text-gray-600">Total Paid Revenue: <span className="font-bold text-green-600">{formatPrice(totals.paid)}</span> from {totals.paidCount} transactions</p>
          <p className="text-sm text-gray-600">Total Unpaid Revenue: {formatPrice(totals.unpaid)} from {totals.unpaidCount} transactions</p>
        </div>
      )}
      
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Transaction #</TableHead>
            <TableHead>Product</TableHead>
            <TableHead>Plan</TableHead>
            <TableHead>Plan Price</TableHead>
            <TableHead>Tax ({taxRate}%)</TableHead>
            <TableHead>Discount</TableHead>
            <TableHead>Total</TableHead>
            <TableHead>Due Date</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className="[&>tr:last-child]:border-0">
          {loading ? (
            <TableRow>
              <TableCell colSpan={10} className="text-center py-4">
                Loading...
              </TableCell>
            </TableRow>
          ) : bills.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} className="text-center py-4">
                No transactions found
              </TableCell>
            </TableRow>
          ) : (
            bills.map((bill) => {
              return (
                <TableRow key={bill.id}>
                  <TableCell>{bill.transaction_number}</TableCell>
                  <TableCell>{bill.plan?.product?.name}</TableCell>
                  <TableCell>{bill.plan?.name}</TableCell>
                  <TableCell>{formatPrice(bill.plan_price || bill.plan?.price || 0)}</TableCell>
                  <TableCell>{formatPrice(bill.tax)}</TableCell>
                  <TableCell>{formatDiscountDisplay(bill)}</TableCell>
                  <TableCell className="font-medium">
                    {formatPrice(bill.amount)}
                  </TableCell>
                  <TableCell>
                    {new Date(bill.due_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(bill.status)}>
                      {bill.status.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleViewInvoice(bill)}
                      className="flex items-center gap-1"
                    >
                      <FileText className="h-4 w-4" />
                      Invoice
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {selectedBill && (
        <InvoiceDialog
          bill={selectedBill as any}
          open={isInvoiceDialogOpen}
          onOpenChange={setIsInvoiceDialogOpen}
        />
      )}
    </div>
  );
}
